import assert from 'minimalistic-assert';
import lodash from 'lodash';
import { UnitTypeIdSymbol } from './constants.js';
import { create as createUnit } from './unit.js';
import isUnitType from './is-unit-type.js';
const { isEmpty, isObject, mapValues, memoize } = lodash;
const FACTORY_SYMBOL = Symbol('UnitType');
const normalizeDefinitions = ({ definitions }) => {
    assert(isObject(definitions), 'definitions must be an object');
    return Object.fromEntries(Object.entries(definitions).sort((a, b) => a[1] - b[1]));
};
export default class UnitType {
    static create = memoize((definitions) => new UnitType(normalizeDefinitions({ definitions }), FACTORY_SYMBOL), (definitions) => JSON.stringify(normalizeDefinitions({ definitions })));
    static get [UnitTypeIdSymbol]() {
        return 'UnitType';
    }
    static isInstance(instance) {
        return this[Symbol.hasInstance](instance);
    }
    static [Symbol.hasInstance](instance) {
        const obj = instance;
        if (!obj?.constructor || Object.hasOwn(obj, 'constructor')) {
            return false;
        }
        if (Function.prototype[Symbol.hasInstance].call(UnitType, instance)) {
            return true;
        }
        return obj.constructor[UnitTypeIdSymbol] === UnitType[UnitTypeIdSymbol];
    }
    static equals(a, b) {
        assert(a instanceof UnitType, 'argument must be a UnitType');
        return a === b || a.equals(b);
    }
    static isUnitType = isUnitType;
    units = Object.create(null);
    baseUnit;
    defaultUnit;
    #zero;
    constructor(definitions, initSymbol) {
        assert(initSymbol === FACTORY_SYMBOL, 'please use UnitType.create()');
        assert(!isEmpty(definitions), 'definitions must have at least one item');
        Object.keys(definitions).forEach((key) => {
            this.units[key] = createUnit(this, key, definitions[key]);
            Reflect.set(this, key, this.units[key]);
        });
        const baseUnits = Object.keys(this.units).filter((unit) => this.units[unit].power === 0);
        if (baseUnits.length === 0)
            throw new Error('Must specify at least one unit with a power of 0.');
        this.baseUnit = this.units[baseUnits[0]];
        this.defaultUnit = Object.values(this.units).reduce((maxUnit, unit) => {
            return Math.trunc(maxUnit.power) > unit.power ? maxUnit : unit;
        }, Object.create(null));
        this.#zero = this.defaultUnit(0);
    }
    equals(other) {
        assert(isUnitType(other), 'argument must be a UnitType');
        return this === other || this.toString() === other.toString();
    }
    parse(str) {
        const [amount, unit] = str.split(' ');
        assert(this.units[unit] !== undefined, `Unit "${unit}" not found from parsing "${str}"`);
        return this.units[unit](amount);
    }
    toString() {
        return String(this.defaultUnit);
    }
    toJSON() {
        return mapValues(this.units, (u) => u.power);
    }
    get ZERO() {
        return this.#zero;
    }
}
